import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Col, Row, Overlay, Popover } from "react-bootstrap";
import Child from "./item";
import { withNavigation } from "../../../../utils/wrapper";
import Hamburger from "../../../../layout/kebab";

const makeClientId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const normalizeChild = (child = {}) => ({
  ...(child || {}),
  _cid: child?._cid,
  Person: {
    ...(child?.Person || {}),
  },
});

function ensureIds(arr) {
  return (Array.isArray(arr) ? arr : []).map((child) => {
    const normalized = normalizeChild(child);
    if (normalized?.uuid || normalized?._cid) return normalized;
    return { ...normalized, _cid: makeClientId() };
  });
}

const flattenChildSuggestions = (child = {}) => ({
  uuid: child?.uuid,
  _cid: child?._cid,
  legalCustody: child?.legalCustody,
  physicalCustody: child?.physicalCustody,
  "Person.firstName": child?.Person?.firstName,
  "Person.middleName": child?.Person?.middleName,
  "Person.lastName": child?.Person?.lastName,
  "Person.generation": child?.Person?.generation,
  "Person.ssn": child?.Person?.ssn,
  "Person.birthDate": child?.Person?.birthDate,
  "Person.gender": child?.Person?.gender,
});

function Children(props) {
  const {
    children,
    onChange,
    suggestions,
    onApplySuggestion,
    onRejectSuggestion,
  } = props;

  const [childrenState, setChildrenState] = useState(() => ensureIds(children));
  const [menuState, setMenuState] = useState({
    show: false,
    target: null,
  });

  const syncingFromPropsRef = useRef(false);

  useEffect(() => {
    syncingFromPropsRef.current = true;
    setChildrenState(ensureIds(children));

    Promise.resolve().then(() => {
      syncingFromPropsRef.current = false;
    });
  }, [children]);

  useEffect(() => {
    if (!onChange) return;
    if (syncingFromPropsRef.current) return;
    onChange({ Children: childrenState });
  }, [childrenState, onChange]);

  const closeHamburger = useCallback(() => {
    setMenuState({
      show: false,
      target: null,
    });
  }, []);

  const onChildAdd = useCallback(
    (e) => {
      e?.preventDefault?.();

      setChildrenState((prev) => [
        ...ensureIds(prev),
        normalizeChild({ _cid: makeClientId() }),
      ]);

      closeHamburger();
    },
    [closeHamburger]
  );

  const onChildChange = useCallback(({ index, values }) => {
    setChildrenState((prev) => {
      const next = [...ensureIds(prev)];
      const current = normalizeChild(next[index] || {});
      const incoming = normalizeChild(values || {});

      next[index] = {
        ...current,
        ...incoming,
        Person: {
          ...(current?.Person || {}),
          ...(incoming?.Person || {}),
        },
      };

      return next;
    });
  }, []);

  const onChildDelete = useCallback(({ index }) => {
    setChildrenState((prev) => {
      const next = [...ensureIds(prev)];
      next.splice(index, 1);
      return next;
    });
  }, []);

  const openHamburger = useCallback((event) => {
    event.preventDefault();
    setMenuState({
      show: true,
      target: event.currentTarget,
    });
  }, []);

  const normalizedSuggestions = useMemo(() => {
    return Array.isArray(suggestions) ? suggestions.map(normalizeChild) : [];
  }, [suggestions]);

  const findSuggestionForChild = useCallback((child, index) => {
    const normalizedChild = normalizeChild(child);

    let match = null;

    if (normalizedChild?.uuid) {
      match = normalizedSuggestions.find((item) => item?.uuid && item.uuid === normalizedChild.uuid);
    }

    if (!match && normalizedChild?._cid) {
      match = normalizedSuggestions.find((item) => item?._cid && item._cid === normalizedChild._cid);
    }

    if (!match) {
      match = normalizedSuggestions[index] || null;
    }

    return match ? flattenChildSuggestions(match) : {};
  }, [normalizedSuggestions]);

  const applyChildSuggestion = useCallback((index, path, value) => {
    if (onApplySuggestion) {
      onApplySuggestion(index, path, value);
      return;
    }

    setChildrenState((prev) => {
      const next = [...ensureIds(prev)];
      const current = normalizeChild(next[index] || {});

      const updated = { ...current };
      const parts = String(path).split(".");
      let ptr = updated;

      for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        ptr[key] = typeof ptr[key] === "object" && ptr[key] !== null ? { ...ptr[key] } : {};
        ptr = ptr[key];
      }

      ptr[parts[parts.length - 1]] = value;
      next[index] = updated;
      return next;
    });
  }, [onApplySuggestion]);

  const rejectChildSuggestion = useCallback((index, path, value) => {
    if (onRejectSuggestion) {
      onRejectSuggestion(index, path, value);
    }
  }, [onRejectSuggestion]);

  return (
    <>
      <Col xs="12">
        <Row className="g-3">
          <Col xs="11">
            <h2 className="small-title">
              Children &nbsp;
            </h2>
          </Col>
          <Col xs="1">
            <Hamburger
              variant="link"
              vertical={false}
              onClick={(e) => openHamburger(e)}
            />
          </Col>
        </Row>

        {Array.isArray(childrenState) &&
          childrenState.map((item, index) => (
            <Child
              key={item?.uuid || item?._cid || `child.${index}`}
              {...item}
              index={index}
              backgroundColor={index % 2 === 0 ? "#e9f8fb" : "white"}
              suggestions={findSuggestionForChild(item, index)}
              onApplySuggestion={(path, value) => applyChildSuggestion(index, path, value)}
              onRejectSuggestion={(path, value) => rejectChildSuggestion(index, path, value)}
              onChange={onChildChange}
              onDelete={onChildDelete}
            />
          ))}
      </Col>

      <Overlay
        target={menuState.target}
        show={menuState.show}
        placement="bottom-end"
        rootClose
        onHide={closeHamburger}
      >
        {(overlayProps) => (
          <Popover {...overlayProps}>
            <Popover.Body>
              <Row className="mb-2">
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start"
                    onClick={onChildAdd}
                  >
                    New
                  </button>
                </Col>
              </Row>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </>
  );
}

Children.propTypes = {
  auth: PropTypes.object,
  children: PropTypes.array,
  onChange: PropTypes.func,
  suggestions: PropTypes.array,
  onApplySuggestion: PropTypes.func,
  onRejectSuggestion: PropTypes.func,
};

Children.defaultProps = {
  children: [],
  onChange: null,
  suggestions: [],
  onApplySuggestion: null,
  onRejectSuggestion: null,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(mapStateToProps, null, null, { forwardRef: true })(Children)
);